import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { 
  Tv, PenTool, ShieldCheck, CheckCircle, HelpCircle, 
  MapPin, Phone, Calendar, Mail, FileText, AlertTriangle, 
  Check, Lock, CreditCard, ChevronRight, MessageSquare, Clock, ArrowRight, Star,
  Award, Wrench, MessageCircle, Zap, MonitorPlay, ClipboardCheck
} from "lucide-react";
import { HOME, DASHBOARD } from "@/constants/testIds";
import { SERVICES as SEO_SERVICES, BRANDS as SEO_BRANDS } from "@/seoData";
import { gtagEvent, gtagConversion, gtagPageview } from "@/analytics";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import Seo from "@/Seo";
import { localBusinessLd, faqPageLd, reviewsLd, HOMEPAGE_FAQS } from "@/seoSchema";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ISSUES = [
  "Screen Damage / Cracks",
  "Sound but No Picture (Backlight)",
  "Power Issue (Won't turn on)",
  "Lines on Screen / Display Glitch",
  "HDMI / Port Damage",
  "Other / Not Sure"
];

const BRANDS = ["Samsung", "LG", "Sony", "TCL", "Mi / Xiaomi", "OnePlus", "Vu", "Panasonic", "Hisense", "Other"];

const AREAS = ["Indiranagar","Koramangala","HSR Layout","BTM Layout","Jayanagar","JP Nagar","Whitefield","Marathahalli","Electronic City","Banashankari","Basavanagudi","Hebbal","KR Puram","Malleshwaram","Rajajinagar","Yelahanka","Bellandur","Sarjapur Road","Mahadevapura","Hennur","Bommanahalli","Vijayanagar","Yeshwanthpur","Banaswadi","Kengeri","Sadashiva Nagar","Anjanapura","Kumaraswamy Layout","Padmanabhanagar","RT Nagar","Sahakar Nagar","Kalyan Nagar","CV Raman Nagar","Bommasandra","HBR Layout","Frazer Town","Cox Town","Cooke Town","Richmond Town","Shivaji Nagar","MG Road","Brigade Road","Ulsoor","Domlur","Murugeshpalya","HAL Layout","Vimanapura","Kammanahalli","Horamavu","TC Palya","Ramamurthy Nagar","Vidyaranyapura","Jalahalli","Peenya","Nagarbhavi","Magadi Road","Rajarajeshwari Nagar","Nayandahalli","Uttarahalli","Konanakunte","Gottigere","Bannerghatta Road","Arekere","Hulimavu","Begur","Hosur Road","Singasandra","Hongasandra","Wilson Garden","Lalbagh Road","Chamarajpet","Gandhi Nagar","Seshadripuram","Mathikere","Sanjaynagar","Thanisandra","Nagavara","Jakkur","Vasanth Nagar","Langford Town","Austin Town","Benson Town","Kasturinagar","Doddanekkundi","Kadugodi","Varthur","Hoodi","ITPL","Brookefield","AECS Layout","Munnekollal","Kundalahalli","Chandapura","Attibele","Jigani","Anekal","Kanakapura Road","Talaghattapura","Subramanyapura","ISRO Layout","Girinagar","Hanumanthnagar","Kathriguppe","Deepanjali Nagar","Nandini Layout","Mahalakshmi Layout","Tumkur Road","Goraguntepalya","Laggere","Kamakshipalya","Sunkadakatte","Basaveshwaranagar"];

const STATUSES = [
  "Pending", 
  "Diagnostics", 
  "Parts Ordered", 
  "In Repair", 
  "Ready for Pickup", 
  "Completed", 
  "Cancelled"
];

export default function App() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("home"); // "home", "book", "dashboard", "reviews"
  const [reviews, setReviews] = useState([]);
  const [bookingError, setBookingError] = useState("");
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [submittingBooking, setSubmittingBooking] = useState(false);
  const [lastJobId, setLastJobId] = useState("");
  const [showAllAreas, setShowAllAreas] = useState(false);
  
  // New Booking State
  const [bookingForm, setBookingForm] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    tv_brand: "Samsung",
    tv_model: "",
    tv_size: "55 inches",
    issue_type: "Power Issue (Won't turn on)",
    issue_description: "",
    service_type: "In-Home",
    booking_date: ""
  });

  // Review State
  const [reviewForm, setReviewForm] = useState({
    client_name: "",
    tv_model: "",
    rating: 5,
    comment: ""
  });

  // Fetch Public Reviews
  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews`);
      setReviews(res.data);
    } catch (err) {
      // Silent failure — reviews section will render an empty state.
    }
  };

  useEffect(() => {
    fetchReviews();
    // Fire Google Ads page-view conversion once per session-load of the home site.
    gtagPageview("/");
  }, []);

  useEffect(() => {
    if (searchParams.get("action") === "book") {
      setActiveTab("book");
      setBookingSuccess(false);
    }
  }, [searchParams]);

  // Submit Booking Form
  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (submittingBooking) return; // in-flight guard: block double-clicks
    setBookingError("");

    // Simple Validation
    if (!bookingForm.client_name || !bookingForm.client_email || !bookingForm.client_phone || !bookingForm.booking_date) {
      setBookingError("Please fill out all required customer information fields.");
      return;
    }

    setSubmittingBooking(true);
    try {
      const res = await axios.post(`${API}/bookings`, bookingForm);
      setLastJobId(res.data.job_id);
      setBookingSuccess(true);
      gtagEvent("book_service", { job_id: res.data.job_id, value: res.data.estimated_cost, currency: "INR" });
      gtagConversion("book", { value: res.data.estimated_cost, currency: "INR" });
      toast.success("Booking request received! We'll call you shortly.");
      window.scrollTo(0, 0);
      // Reset form
      setBookingForm({
        client_name: "",
        client_email: "",
        client_phone: "",
        tv_brand: "Samsung",
        tv_model: "",
        tv_size: "55 inches",
        issue_type: "Power Issue (Won't turn on)",
        issue_description: "",
        service_type: "In-Home",
        booking_date: ""
      });
    } catch (err) {
      setBookingError("Failed to book service. Please check API connection.");
    } finally {
      setSubmittingBooking(false);
    }
  };

  // Submit Review Form
  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!reviewForm.client_name || !reviewForm.tv_model || !reviewForm.comment) {
      toast.error("Please fill in all review fields.");
      return;
    }
    try {
      await axios.post(`${API}/reviews`, reviewForm);
      toast.success("Review posted successfully! Thank you.");
      setReviewForm({
        client_name: "",
        tv_model: "",
        rating: 5,
        comment: ""
      });
      fetchReviews();
    } catch (err) {
      toast.error("Failed to submit review");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col antialiased">
      <Seo
        title="TV Repair in Bangalore | Same-Day Doorstep Service | TV CARE"
        description="Same-day TV repair in Bangalore at your doorstep. LED, LCD, OLED, QLED, Smart & 4K TVs fixed by trusted engineers with a six-month warranty. Call (888) 434-0007 for a free quote."
        path="/"
        jsonLd={[
          localBusinessLd(),
          faqPageLd(HOMEPAGE_FAQS),
          ...(reviewsLd(reviews) ? [reviewsLd(reviews)] : []),
        ]}
      />
      <Toaster position="top-right" />

      {/* FLOATING WHATSAPP BUTTON */}
      <a
        href="https://wa.me/918884340007?text=Hi%20TV%20CARE%2C%20I%20need%20TV%20repair%20service"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-float-btn"
        aria-label="Chat on WhatsApp"
        onClick={() => { gtagEvent("whatsapp_click"); gtagConversion("whatsapp"); }}
        className="fixed bottom-6 left-6 z-[60] flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366] shadow-lg shadow-black/20 hover:scale-110 transition-transform"
      >
        <svg viewBox="0 0 32 32" className="w-7 h-7" fill="#ffffff" aria-hidden="true">
          <path d="M16.001 3.2c-7.06 0-12.8 5.74-12.8 12.8 0 2.257.59 4.46 1.71 6.4L3.2 28.8l6.57-1.72a12.74 12.74 0 0 0 6.23 1.62h.005c7.06 0 12.8-5.74 12.8-12.8s-5.74-12.7-12.8-12.7zm0 23.04h-.004a10.6 10.6 0 0 1-5.4-1.48l-.388-.23-4.003 1.05 1.068-3.9-.253-.4a10.58 10.58 0 0 1-1.62-5.64c0-5.86 4.77-10.63 10.64-10.63 2.84 0 5.51 1.11 7.52 3.12a10.56 10.56 0 0 1 3.11 7.52c0 5.87-4.77 10.6-10.64 10.6zm5.83-7.94c-.32-.16-1.89-.93-2.18-1.04-.29-.11-.5-.16-.71.16-.21.32-.82 1.04-1.005 1.25-.185.21-.37.24-.69.08-.32-.16-1.35-.5-2.57-1.59-.95-.85-1.59-1.9-1.78-2.22-.185-.32-.02-.49.14-.65.144-.143.32-.37.48-.56.16-.19.21-.32.32-.53.106-.21.053-.4-.027-.56-.08-.16-.71-1.71-.97-2.35-.256-.616-.516-.53-.71-.54l-.605-.01c-.21 0-.55.08-.84.4-.29.32-1.1 1.08-1.1 2.63s1.13 3.05 1.29 3.26c.16.21 2.22 3.39 5.38 4.75.75.32 1.34.51 1.8.66.756.24 1.444.206 1.99.125.607-.09 1.89-.773 2.156-1.52.266-.747.266-1.387.186-1.52-.08-.133-.29-.21-.61-.37z"/>
        </svg>
      </a>

      
      {/* HEADER NAVBAR */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm backdrop-blur-md bg-white/95">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => setActiveTab("home")}>
            <div className="p-2 bg-[#0a2540] rounded-lg text-white">
              <Tv className="h-6 w-6" />
            </div>
            <div>
              <span className="font-extrabold text-xl tracking-tight text-[#0a2540] block leading-none">TV CARE</span>
              <span className="text-[9px] text-slate-500 font-medium block mt-0.5 uppercase tracking-wider">Your Tv Our Care</span>
            </div>
          </div>
          
          <nav className="hidden md:flex items-center gap-1">
            <button 
              onClick={() => setActiveTab("home")} 
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === 'home' ? 'bg-slate-100 text-[#0a2540]' : 'text-slate-600 hover:text-[#0a2540]'}`}
            >
              Home
            </button>
            <button 
              onClick={() => setActiveTab("book")} 
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === 'book' ? 'bg-slate-100 text-[#0a2540]' : 'text-slate-600 hover:text-[#0a2540]'}`}
            >
              Book Repair
            </button>
          </nav>
          
          <div className="flex items-center gap-3">
            <a 
              href="tel:8884340007"
              data-testid="navbar-phone-link"
              onClick={() => { gtagEvent("call_click", { location: "navbar" }); gtagConversion("call"); }}
              className="flex items-center gap-2.5 bg-[#0a2540] hover:bg-slate-800 text-white rounded-xl h-10 px-5 text-sm font-semibold shadow-sm transition-all"
            >
              <Phone className="h-4 w-4 text-[#f97316]" />
              <span className="hidden sm:inline">(888) 434-0007</span>
              <span className="sm:hidden">Call</span>
            </a>
          </div>
        </div>
      </header>

      {/* MAIN CONTENT AREA */}
      <main className="flex-1">
        
        {/* TAB 1: HOME PAGE */}
        {activeTab === "home" && (
          <div>
            {/* HERO SECTION */}
            <section className="relative bg-[#0a2540] overflow-hidden">
              <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_80%_20%,rgba(249,115,22,0.25),transparent_45%)]"></div>
              <div className="relative max-w-7xl mx-auto px-6 py-16 sm:py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className="text-left space-y-6">
                  <h1 data-testid={HOME.heroTitle} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
                    <span className="text-white">EXPERT TV</span><br />
                    <span className="text-[#f97316]">REPAIR &amp; SERVICE</span>
                  </h1>
                  <p className="text-lg text-slate-300 leading-relaxed max-w-xl">
                    Quick, Reliable &amp; Affordable TV Repair Service at Your Doorstep
                  </p>
                  <ul className="space-y-3.5 pt-1">
                    {[
                      "LED, LCD, OLED & QLED TV Repair",
                      "Motherboard & Power Supply Repair",
                      "Display Replacement",
                      "Software Update & Installation",
                      "Doorstep Service Available",
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-slate-100 text-base font-medium">
                        <span className="w-6 h-6 rounded-full bg-[#f97316] flex items-center justify-center flex-shrink-0">
                          <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <Button 
                      data-testid={HOME.heroBookingBtn}
                      onClick={() => setActiveTab("book")}
                      className="bg-[#f97316] hover:bg-[#ea580c] text-white rounded-lg h-12 px-8 text-sm font-bold uppercase tracking-wide shadow-lg shadow-[#f97316]/20 transition-all flex items-center gap-2"
                    >
                      Book a Service <ArrowRight className="h-5 w-5" />
                    </Button>
                    <button 
                      data-testid="hero-services-btn"
                      onClick={() => document.getElementById("services-section")?.scrollIntoView({ behavior: "smooth" })}
                      className="border border-white/30 hover:bg-white/10 text-white rounded-lg h-12 px-8 text-sm font-bold uppercase tracking-wide transition-all flex items-center justify-center gap-2"
                    >
                      Our Services
                    </button>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10">
                    <img 
                      src="https://static.prod-images.emergentagent.com/jobs/b546ad80-490a-4c9f-b5e4-e771166566c2/images/acbe78f2392927969972cc447d5a1a8fe3d8a37736e37f7658bd46e459ac821d.png" 
                      alt="TV CARE technician standing with folded arms in a professional service center" 
                      className="w-full object-cover aspect-[4/3]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a2540]/40 to-transparent"></div>
                  </div>
                  {/* 100% Satisfaction Badge */}
                  <div className="absolute -bottom-5 -right-3 sm:-right-5 w-28 h-28 sm:w-32 sm:h-32 rounded-full bg-[#0a2540] border-4 border-[#f97316] shadow-2xl flex flex-col items-center justify-center text-center">
                    <div className="flex text-[#f97316] gap-0.5 mb-0.5">
                      <Star className="h-3 w-3 fill-[#f97316]" /><Star className="h-3 w-3 fill-[#f97316]" /><Star className="h-3 w-3 fill-[#f97316]" />
                    </div>
                    <span className="text-white font-extrabold text-2xl sm:text-3xl leading-none">100%</span>
                    <span className="text-[#f97316] font-bold text-[9px] sm:text-[10px] uppercase tracking-wider mt-1 leading-tight">Satisfaction<br />Guarantee</span>
                  </div>
                </div>
              </div>

              {/* FEATURE STRIP */}
              <div className="relative border-t border-white/10 bg-[#081d33]">
                <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                  {[
                    { icon: Wrench, title: "Expert Technicians", desc: "Skilled & experienced professionals" },
                    { icon: ShieldCheck, title: "Genuine Parts", desc: "We use original & high quality parts" },
                    { icon: Zap, title: "Fast Service", desc: "Quick response & on-time service" },
                    { icon: Award, title: "Warranty Support", desc: "Service warranty on parts & labour" },
                  ].map((f, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="p-3 rounded-xl bg-[#f97316]/15 text-[#f97316] flex-shrink-0">
                        <f.icon className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="text-white font-bold text-sm uppercase tracking-wide leading-tight">{f.title}</h3>
                        <p className="text-slate-400 text-sm mt-1 leading-relaxed">{f.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* BRANDS WE REPAIR (SEO-optimised, all 26 brands linked and above the fold) */}
            <section id="brands-section" className="py-20 bg-white border-b border-slate-200 scroll-mt-16" data-testid="brands-directory">
              <div className="max-w-7xl mx-auto px-6 text-left">
                <div className="max-w-3xl mb-10">
                  <span className="text-[#f97316] text-xs font-extrabold uppercase tracking-widest block mb-2">Every make covered</span>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0a2540] tracking-tight mb-4">
                    Every Major TV Brand We Repair in Bangalore
                  </h2>
                  <p className="text-base text-slate-600 leading-relaxed">
                    From flagship Sony Bravia and Samsung Neo QLED to budget Vu, Mi and Kodak sets &mdash; our engineers know the boards, the panels and the quirks of every manufacturer sold in India. Pick your brand for same-day doorstep service in your area.
                  </p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {SEO_BRANDS.map((b, i) => (
                    <Link
                      key={b.slug}
                      to={`/${b.slug}`}
                      data-testid={`brand-card-${i}`}
                      className="group flex items-center justify-center bg-[#f8fafc] hover:bg-[#0a2540] border border-slate-200 hover:border-[#0a2540] rounded-xl px-4 py-5 text-center transition-all shadow-sm hover:shadow-md"
                    >
                      <span className="text-sm sm:text-base font-bold text-[#0a2540] group-hover:text-white transition-colors">
                        {b.brand}
                      </span>
                    </Link>
                  ))}
                </div>
                <p className="text-sm text-slate-500 mt-6">
                  Don&apos;t see your brand? We repair almost every television sold in India &mdash; give us a call and we&apos;ll confirm.
                </p>
              </div>
            </section>

            {/* SERVICES DIRECTORY SECTION */}
            <section id="services-section" className="py-20 bg-white scroll-mt-16">
              <div className="max-w-7xl mx-auto px-6 text-left">
                <div className="max-w-3xl mb-14">
                  <span className="text-[#f97316] text-xs font-extrabold uppercase tracking-widest block mb-2">Our expertise</span>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0a2540] tracking-tight mb-4">
                    Every Screen, Every Fault, Sorted
                  </h2>
                  <p className="text-base text-slate-600 leading-relaxed">
                    Whatever sits in your living room &mdash; budget LED, flagship OLED or a giant 4K panel &mdash; our bench-trained engineers know it inside out. Tell us the symptom and we&apos;ll handle the rest, with a clear price agreed up front.
                  </p>
                </div>

                <div className="max-w-3xl mx-auto space-y-3">
                  {[
                    { icon: Tv, title: "LED Televisions", desc: "Dim or dead backlights, blank displays and stubborn standby lights diagnosed down to the board." },
                    { icon: MonitorPlay, title: "LCD Panels", desc: "Washed-out colours, ghosting, dead pixels and crackly audio brought back to crisp condition." },
                    { icon: Zap, title: "OLED & QLED Sets", desc: "Burn-in, panel faults and tricky mainboard issues on premium displays, handled with care." },
                    { icon: Wrench, title: "Smart & Android TVs", desc: "Frozen menus, app crashes, sluggish performance and Wi-Fi dropouts ironed out for good." },
                    { icon: ShieldCheck, title: "Plasma Displays", desc: "Power board trouble and panel glitches on older plasma sets given a new lease of life." },
                    { icon: PenTool, title: "4K & UHD Screens", desc: "HDMI handshake errors, image processor faults and resolution issues resolved properly." },
                  ].map((s, i) => (
                    <details key={i} data-testid={`service-accordion-${i}`} className="group bg-[#f8fafc] border border-slate-200 rounded-xl overflow-hidden transition-all hover:border-[#f97316]/40">
                      <summary className="flex items-center gap-4 cursor-pointer list-none p-5 sm:p-6">
                        <span className="p-2.5 bg-white rounded-xl border border-slate-200 text-[#f97316] shadow-sm flex-shrink-0">
                          <s.icon className="h-5 w-5" />
                        </span>
                        <h3 className="font-bold text-base sm:text-lg text-[#0a2540] flex-1">{s.title}</h3>
                        <ChevronRight className="h-5 w-5 text-slate-400 transition-transform group-open:rotate-90 flex-shrink-0" />
                      </summary>
                      <p className="text-slate-600 text-sm leading-relaxed px-5 sm:px-6 pb-6 pl-[4.25rem]">{s.desc}</p>
                    </details>
                  ))}
                </div>
              </div>
            </section>

            {/* WHY CHOOSE US SECTION */}
            <section className="py-20 bg-[#0a2540] text-white">
              <div className="max-w-7xl mx-auto px-6 text-left">
                <div className="max-w-3xl mb-14">
                  <span className="text-[#f97316] text-xs font-extrabold uppercase tracking-widest block mb-2">The TV Care difference</span>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
                    Why Families Keep Calling Us Back
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[
                    { icon: Award, title: "A Decade on the Bench", desc: "Years of hands-on repairs across thousands of sets, from old tubes to the latest panels." },
                    { icon: ShieldCheck, title: "Vetted Engineers", desc: "Every technician is background-checked, trained and trusted with your prized display." },
                    { icon: ClipboardCheck, title: "Straight Talk on Cost", desc: "You hear the price before we start &mdash; what we say is exactly what you pay." },
                    { icon: MapPin, title: "We Travel to You", desc: "Skip the heavy lifting; we bring the workshop to your home or office instead." },
                  ].map((f, i) => (
                    <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all">
                      <div className="p-3 bg-[#f97316]/15 w-fit rounded-xl text-[#f97316] mb-4">
                        <f.icon className="h-6 w-6" />
                      </div>
                      <h3 className="font-bold text-base mb-2">{f.title}</h3>
                      <p className="text-sm text-slate-300 leading-relaxed">{f.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* 3-STEP PROCESS SECTION */}
            <section className="py-20 bg-white">
              <div className="max-w-7xl mx-auto px-6 text-left">
                <div className="max-w-3xl mb-14">
                  <span className="text-[#f97316] text-xs font-extrabold uppercase tracking-widest block mb-2">Getting started</span>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0a2540] tracking-tight">
                    Three Easy Steps to a Working TV
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {[
                    { n: "01", title: "Tell Us What's Wrong", desc: "Drop us a call or fill in the quick form with your set's make, model and the issue you're seeing." },
                    { n: "02", title: "We Come Knocking", desc: "An engineer arrives at a slot that suits you, tools in hand, to pinpoint the fault on the spot." },
                    { n: "03", title: "Watch It Spring Back", desc: "Most faults are mended there and then, so you're back to your shows before the day is out." },
                  ].map((p, i) => (
                    <Card key={i} className="rounded-xl border border-slate-200 bg-[#f8fafc] p-8 relative overflow-hidden">
                      <span className="absolute -top-2 -right-1 text-7xl font-extrabold text-slate-200/70 select-none">{p.n}</span>
                      <div className="relative">
                        <h3 className="font-bold text-lg text-[#0a2540] mb-2">{p.title}</h3>
                        <p className="text-slate-600 text-sm leading-relaxed">{p.desc}</p>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </section>

            {/* AREAS WE SERVE SECTION */}
            <section className="py-20 bg-[#f8fafc] border-t border-slate-200">
              <div className="max-w-7xl mx-auto px-6 text-left">
                <div className="max-w-3xl mb-10">
                  <span className="text-[#f97316] text-xs font-extrabold uppercase tracking-widest block mb-2">Where we work</span>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0a2540] tracking-tight mb-4">
                    Reaching Neighbourhoods City-Wide
                  </h2>
                  <p className="text-base text-slate-600 leading-relaxed">
                    Our engineers cover homes and offices right across the city. Can&apos;t spot your locality on the list? Give us a ring &mdash; chances are we&apos;ll still be there.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2.5">
                  {(showAllAreas ? AREAS : AREAS.slice(0, 16)).map((area, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-medium text-slate-700 hover:border-[#f97316] hover:text-[#0a2540] transition-all">
                      <MapPin className="h-3.5 w-3.5 text-[#f97316]" /> {area}
                    </span>
                  ))}
                  <button
                    data-testid="areas-toggle-btn"
                    onClick={() => setShowAllAreas(!showAllAreas)}
                    className="inline-flex items-center gap-1.5 bg-[#0a2540] text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-all"
                  >
                    {showAllAreas ? "Show less" : `+ ${AREAS.length - 16} more areas`}
                  </button>
                </div>
              </div>
            </section>

            {/* CUSTOMER REVIEWS PORTION */}
            <section className="bg-slate-50 py-20 border-t border-slate-200">
              <div className="max-w-7xl mx-auto px-6 text-left">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-4">
                  <div>
                    <span className="text-[#f97316] text-xs font-extrabold uppercase tracking-widest block mb-2">Word of mouth</span>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0a2540] tracking-tight">
                      Stories From Our Customers
                    </h2>
                  </div>
                  <Button 
                    onClick={() => setActiveTab("reviews")}
                    className="bg-white border border-slate-300 hover:bg-slate-100 text-[#0a2540] rounded-xl h-10 px-5 text-sm font-semibold shadow-sm transition-all"
                  >
                    Share Your Experience
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {reviews.length > 0 ? (
                    reviews.map((r, i) => (
                      <Card key={r.id || i} className="rounded-xl border border-slate-200 bg-white p-8 shadow-sm flex flex-col justify-between">
                        <div className="space-y-4">
                          <div className="flex text-[#f97316] gap-0.5">
                            {Array.from({ length: r.rating }).map((_, idx) => (
                              <Star key={idx} className="h-5 w-5 fill-[#f97316]" />
                            ))}
                          </div>
                          <p className="text-slate-600 text-sm italic leading-relaxed">
                            &ldquo;{r.comment}&rdquo;
                          </p>
                        </div>
                        <div className="pt-6 border-t border-slate-100 mt-6 text-left">
                          <p className="font-bold text-sm text-[#0a2540]">{r.client_name}</p>
                          <p className="text-xs text-slate-500">{r.tv_model}</p>
                        </div>
                      </Card>
                    ))
                  ) : (
                    <div className="text-slate-400 text-sm col-span-3 text-center py-10">
                      No customer reviews loaded yet.
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* FAQ SECTION */}
            <section className="py-20 bg-white border-t border-slate-200">
              <div className="max-w-4xl mx-auto px-6 text-left">
                <div className="mb-12">
                  <span className="text-[#f97316] text-xs font-extrabold uppercase tracking-widest block mb-2">Good to know</span>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0a2540] tracking-tight">
                    Your Questions, Answered
                  </h2>
                </div>
                <div className="space-y-4">
                  {HOMEPAGE_FAQS.map((item, i) => (
                    <details key={i} data-testid={`faq-item-${i}`} className="group bg-[#f8fafc] border border-slate-200 rounded-xl p-6 transition-all">
                      <summary className="flex justify-between items-center cursor-pointer font-bold text-[#0a2540] list-none">
                        <span className="flex items-center gap-3"><HelpCircle className="h-5 w-5 text-[#f97316] flex-shrink-0" /> {item.question}</span>
                        <ChevronRight className="h-5 w-5 text-slate-400 transition-transform group-open:rotate-90 flex-shrink-0" />
                      </summary>
                      <p className="text-sm text-slate-600 leading-relaxed mt-4 pl-8">{item.answer}</p>
                    </details>
                  ))}
                </div>
              </div>
            </section>

            {/* CALL TO ACTION BANNER */}
            <section className="py-16 bg-[#0a2540]">
              <div className="max-w-4xl mx-auto px-6 text-center">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight mb-3">
                  Ready to Watch Again?
                </h2>
                <p className="text-base text-slate-300 mb-8 max-w-2xl mx-auto">
                  Give us a ring today for a friendly chat and a fair quote. Same-day visits mean you could be back on the sofa, remote in hand, by this evening.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a 
                    href="tel:8884340007"
                    data-testid="cta-call-link"
                    onClick={() => { gtagEvent("call_click", { location: "cta" }); gtagConversion("call"); }}
                    className="bg-[#f97316] hover:bg-[#ea580c] text-white rounded-xl h-12 px-8 text-base font-semibold transition-all flex items-center justify-center gap-2"
                  >
                    <Phone className="h-5 w-5" /> Call (888) 434-0007
                  </a>
                  <Button 
                    onClick={() => setActiveTab("book")}
                    className="bg-white hover:bg-slate-100 text-[#0a2540] rounded-xl h-12 px-8 text-base font-semibold transition-all flex items-center justify-center gap-2"
                  >
                    Request a Visit <ArrowRight className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* TAB 2: BOOKING FORM */}
        {activeTab === "book" && (
          <section className="py-16 bg-[#f8fafc]">
            <div className="max-w-4xl mx-auto px-6 text-left">
              {bookingSuccess ? (
                <Card data-testid="booking-success-card" className="rounded-2xl border border-slate-200 bg-white p-10 sm:p-14 shadow-md text-center">
                  <div className="mx-auto w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mb-6">
                    <CheckCircle className="h-9 w-9 text-emerald-600" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0a2540] tracking-tight mb-3">
                    Thanks &mdash; Your Request Is In!
                  </h2>
                  <p className="text-base text-slate-600 leading-relaxed max-w-xl mx-auto mb-2">
                    We&apos;ve received your repair request{lastJobId ? <span> (ref <span className="font-bold text-[#0a2540]">{lastJobId}</span>)</span> : null}. One of our engineers will call you shortly to confirm a time and share a fair quote.
                  </p>
                  <p className="text-sm text-slate-500 mb-8">
                    Need us sooner? Call <a href="tel:8884340007" className="font-semibold text-[#0a2540] hover:underline">(888) 434-0007</a>.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      data-testid="booking-another-btn"
                      onClick={() => setBookingSuccess(false)}
                      className="bg-[#0a2540] hover:bg-slate-800 text-white rounded-xl h-12 px-8 text-base font-semibold transition-all"
                    >
                      Book Another Repair
                    </Button>
                    <Button
                      onClick={() => setActiveTab("home")}
                      variant="outline"
                      className="border-slate-300 text-[#0a2540] rounded-xl h-12 px-8 text-base font-semibold transition-all"
                    >
                      Back to Home
                    </Button>
                  </div>
                </Card>
              ) : (
              <>
              <div className="mb-10">
                <span className="text-[#f97316] text-xs font-extrabold uppercase tracking-widest block mb-2">Let's get started</span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0a2540] tracking-tight">
                  Book Your Repair in Minutes
                </h2>
                <p className="text-sm text-slate-500 mt-2">
                  Pop in a few details about your set and the trouble it&apos;s giving you. We&apos;ll come back with a clear quote and line up a visit at a time that works for you.
                </p>
              </div>

              <Card className="rounded-xl border border-slate-200 bg-white p-8 sm:p-12 shadow-md">
                <form onSubmit={handleBookingSubmit} className="space-y-8">
                  {/* Step 1: TV Details */}
                  <div>
                    <h3 className="text-lg font-bold text-[#0a2540] mb-4 flex items-center gap-2 pb-2 border-b border-slate-100">
                      <Tv className="h-5 w-5 text-[#f97316]" /> 1. Television System Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="tv-brand" className="font-semibold text-slate-700">TV Manufacturer / Brand *</Label>
                        <select 
                          id="tv-brand"
                          data-testid={HOME.bookingBrand}
                          value={bookingForm.tv_brand}
                          onChange={(e) => setBookingForm({...bookingForm, tv_brand: e.target.value})}
                          className="w-full rounded-xl border border-slate-300 bg-white h-11 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a2540]"
                        >
                          {BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="tv-model" className="font-semibold text-slate-700">Model Number (optional)</Label>
                        <Input 
                          id="tv-model"
                          data-testid={HOME.bookingModel}
                          placeholder="e.g. OLED65C1PUB"
                          value={bookingForm.tv_model}
                          onChange={(e) => setBookingForm({...bookingForm, tv_model: e.target.value})}
                          className="rounded-xl h-11 border-slate-300 focus:ring-2 focus:ring-[#0a2540]"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="tv-size" className="font-semibold text-slate-700">Diagonal Size *</Label>
                        <select 
                          id="tv-size"
                          data-testid={HOME.bookingSize}
                          value={bookingForm.tv_size}
                          onChange={(e) => setBookingForm({...bookingForm, tv_size: e.target.value})}
                          className="w-full rounded-xl border border-slate-300 bg-white h-11 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a2540]"
                        >
                          <option value="32&quot;">32 inches or smaller</option>
                          <option value="43&quot;">43 inches</option>
                          <option value="55&quot;">55 inches</option>
                          <option value="65&quot;">65 inches</option>
                          <option value="75&quot;">75 inches</option>
                          <option value="85&quot;">85 inches or larger</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Step 2: Issue Details */}
                  <div>
                    <h3 className="text-lg font-bold text-[#0a2540] mb-4 flex items-center gap-2 pb-2 border-b border-slate-100">
                      <AlertTriangle className="h-5 w-5 text-[#f97316]" /> 2. Diagnosis Request Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="issue-type" className="font-semibold text-slate-700">Primary Failure Symptom *</Label>
                        <select 
                          id="issue-type"
                          data-testid={HOME.bookingIssue}
                          value={bookingForm.issue_type}
                          onChange={(e) => setBookingForm({...bookingForm, issue_type: e.target.value})}
                          className="w-full rounded-xl border border-slate-300 bg-white h-11 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a2540]"
                        >
                          {ISSUES.map((i) => <option key={i} value={i}>{i}</option>)}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="service-type" className="font-semibold text-slate-700">Preferred Logistics Location *</Label>
                        <select 
                          id="service-type"
                          data-testid={HOME.bookingServiceType}
                          value={bookingForm.service_type}
                          onChange={(e) => setBookingForm({...bookingForm, service_type: e.target.value})}
                          className="w-full rounded-xl border border-slate-300 bg-white h-11 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a2540]"
                        >
                          <option value="In-Home">In-Home Visit (Technician comes to you)</option>
                          <option value="Carry-In">Carry-In Dropoff (You drop off in our shop)</option>
                        </select>
                      </div>

                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="issue-desc" className="font-semibold text-slate-700">Detailed Symptom Description *</Label>
                        <textarea 
                          id="issue-desc"
                          data-testid={HOME.bookingDesc}
                          rows={4}
                          placeholder="Please describe exactly what happens. e.g. 'Red standby LED light blinks twice but screen remains dark when pressing power button...'"
                          value={bookingForm.issue_description}
                          onChange={(e) => setBookingForm({...bookingForm, issue_description: e.target.value})}
                          className="w-full rounded-xl border border-slate-300 bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a2540]"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Step 3: Customer Info */}
                  <div>
                    <h3 className="text-lg font-bold text-[#0a2540] mb-4 flex items-center gap-2 pb-2 border-b border-slate-100">
                      <PenTool className="h-5 w-5 text-[#f97316]" /> 3. Contact & Logistics Scheduling
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="client-name" className="font-semibold text-slate-700">Full Name *</Label>
                        <Input 
                          id="client-name"
                          data-testid={HOME.bookingName}
                          placeholder="Jane Doe"
                          value={bookingForm.client_name}
                          onChange={(e) => setBookingForm({...bookingForm, client_name: e.target.value})}
                          className="rounded-xl h-11 border-slate-300 focus:ring-2 focus:ring-[#0a2540]"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="client-email" className="font-semibold text-slate-700">Email Address *</Label>
                        <Input 
                          id="client-email"
                          data-testid={HOME.bookingEmail}
                          type="email"
                          placeholder="jane.doe@example.com"
                          value={bookingForm.client_email}
                          onChange={(e) => setBookingForm({...bookingForm, client_email: e.target.value})}
                          className="rounded-xl h-11 border-slate-300 focus:ring-2 focus:ring-[#0a2540]"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="client-phone" className="font-semibold text-slate-700">Phone Number *</Label>
                        <Input 
                          id="client-phone"
                          data-testid={HOME.bookingPhone}
                          placeholder="e.g. (555) 123-4567"
                          value={bookingForm.client_phone}
                          onChange={(e) => setBookingForm({...bookingForm, client_phone: e.target.value})}
                          className="rounded-xl h-11 border-slate-300 focus:ring-2 focus:ring-[#0a2540]"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="booking-date" className="font-semibold text-slate-700">Preferred Appointment Date *</Label>
                        <Input 
                          id="booking-date"
                          data-testid={HOME.bookingDate}
                          type="date"
                          value={bookingForm.booking_date}
                          onChange={(e) => setBookingForm({...bookingForm, booking_date: e.target.value})}
                          className="rounded-xl h-11 border-slate-300 focus:ring-2 focus:ring-[#0a2540]"
                        />
                      </div>
                    </div>
                  </div>

                  {bookingError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm font-medium">
                      {bookingError}
                    </div>
                  )}

                  <div className="pt-4 text-left">
                    <Button 
                      data-testid={HOME.bookingSubmitBtn}
                      type="submit"
                      disabled={submittingBooking}
                      className="bg-[#0a2540] hover:bg-slate-800 text-white rounded-xl h-12 px-10 text-base font-semibold shadow-md transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {submittingBooking ? "Submitting…" : "Submit Repair Request"}
                    </Button>
                  </div>
                </form>
              </Card>
              </>
              )}
            </div>
          </section>
        )}


        {/* TAB 5: PUBLIC FEEDBACK & REVIEWS AREA */}
        {activeTab === "reviews" && (
          <section className="py-16 bg-slate-50">
            <div className="max-w-4xl mx-auto px-6 text-left">
              <div className="mb-10 text-center">
                <span className="text-[#f97316] text-xs font-extrabold uppercase tracking-widest block mb-2">Tell us how we did</span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0a2540]">Share Your Experience</h2>
                <p className="text-sm text-slate-500 mt-2">Had your set fixed by our team? Leave a few words below and help other households know what to expect.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
                
                {/* Review Form */}
                <div className="md:col-span-5">
                  <Card className="rounded-xl border border-slate-200 bg-white p-6 shadow-md">
                    <h3 className="text-lg font-bold text-[#0a2540] mb-4">Leave Your Feedback</h3>
                    <form onSubmit={handleReviewSubmit} className="space-y-4">
                      <div className="space-y-1">
                        <Label htmlFor="rev-name" className="font-semibold text-xs text-slate-700">Your Full Name</Label>
                        <Input 
                          id="rev-name"
                          data-testid={DASHBOARD.reviewClientInput}
                          placeholder="Arthur Dent"
                          value={reviewForm.client_name}
                          onChange={(e) => setReviewForm({...reviewForm, client_name: e.target.value})}
                          className="rounded-xl h-10 border-slate-300 focus:ring-2 focus:ring-[#0a2540]"
                        />
                      </div>

                      <div className="space-y-1">
                        <Label htmlFor="rev-model" className="font-semibold text-xs text-slate-700">TV Brand & Model</Label>
                        <Input 
                          id="rev-model"
                          data-testid={DASHBOARD.reviewTvModelInput}
                          placeholder="Sony Bravia 55 inches"
                          value={reviewForm.tv_model}
                          onChange={(e) => setReviewForm({...reviewForm, tv_model: e.target.value})}
                          className="rounded-xl h-10 border-slate-300 focus:ring-2 focus:ring-[#0a2540]"
                        />
                      </div>

                      <div className="space-y-1">
                        <Label htmlFor="rev-rating" className="font-semibold text-xs text-slate-700">Rating Satisfaction</Label>
                        <select 
                          id="rev-rating"
                          data-testid={DASHBOARD.reviewRatingSelect}
                          value={reviewForm.rating}
                          onChange={(e) => setReviewForm({...reviewForm, rating: parseInt(e.target.value)})}
                          className="w-full rounded-xl border border-slate-300 bg-white h-10 px-3 text-sm focus:outline-none"
                        >
                          <option value={5}>5 Stars - Outstanding</option>
                          <option value={4}>4 Stars - Great Service</option>
                          <option value={3}>3 Stars - Average</option>
                          <option value={2}>2 Stars - Poor</option>
                          <option value={1}>1 Star - Dissatisfied</option>
                        </select>
                      </div>

                      <div className="space-y-1">
                        <Label htmlFor="rev-comment" className="font-semibold text-xs text-slate-700">Review Feedback</Label>
                        <textarea 
                          id="rev-comment"
                          data-testid={DASHBOARD.reviewCommentInput}
                          rows={4}
                          placeholder="Write your review here..."
                          value={reviewForm.comment}
                          onChange={(e) => setReviewForm({...reviewForm, comment: e.target.value})}
                          className="w-full rounded-xl border border-slate-300 bg-white p-3 text-sm focus:outline-none"
                        />
                      </div>

                      <Button 
                        data-testid={DASHBOARD.reviewSubmitBtn}
                        type="submit"
                        className="w-full bg-[#0a2540] hover:bg-slate-800 text-white rounded-xl h-10 text-sm font-semibold shadow-sm transition-all"
                      >
                        Submit Feedback Review
                      </Button>
                    </form>
                  </Card>
                </div>

                {/* Reviews List */}
                <div className="md:col-span-7 space-y-6">
                  {reviews.length > 0 ? (
                    reviews.map((r, i) => (
                      <Card key={r.id || i} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex text-[#f97316] gap-0.5">
                            {Array.from({ length: r.rating }).map((_, idx) => (
                              <Star key={idx} className="h-4 w-4 fill-[#f97316]" />
                            ))}
                          </div>
                          <span className="text-xs text-slate-400">
                            {r.created_at ? new Date(r.created_at).toLocaleDateString() : 'Just now'}
                          </span>
                        </div>
                        <p className="text-slate-700 text-sm italic mb-4">&ldquo;{r.comment}&rdquo;</p>
                        <div className="pt-3 border-t border-slate-100 flex justify-between items-center text-xs">
                          <span className="font-bold text-[#0a2540]">{r.client_name}</span>
                          <span className="text-slate-500 font-medium">{r.tv_model}</span>
                        </div>
                      </Card>
                    ))
                  ) : (
                    <div className="text-slate-400 text-sm py-10 text-center">
                      No reviews found. Be the first to write one!
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* OUR SERVICES DIRECTORY */}
      <section className="bg-slate-50 border-t border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-6 text-left">
          <div className="mb-10">
            <span className="text-[#f97316] text-xs font-extrabold uppercase tracking-widest block mb-2">Our services</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0a2540] tracking-tight">
              Everything We Fix &amp; Every Brand We Service
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Service Types */}
            <div>
              <h3 className="text-sm font-extrabold text-[#0a2540] uppercase tracking-wider mb-4 flex items-center gap-2">
                <Wrench className="h-4 w-4 text-[#f97316]" /> TV Repair Services
              </h3>
              <div className="flex flex-wrap gap-2.5">
                {SEO_SERVICES.map((s, i) => (
                  <Link
                    key={i}
                    data-testid={`service-link-${i}`}
                    to={`/${s.slug}`}
                    className="inline-flex items-center bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-medium text-slate-700 hover:border-[#f97316] hover:text-[#0a2540] transition-all"
                  >
                    {s.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Brand Services */}
            <div>
              <h3 className="text-sm font-extrabold text-[#0a2540] uppercase tracking-wider mb-4 flex items-center gap-2">
                <Tv className="h-4 w-4 text-[#f97316]" /> Brands We Service
              </h3>
              <div className="flex flex-wrap gap-2.5">
                {SEO_BRANDS.map((b, i) => (
                  <Link
                    key={i}
                    data-testid={`brand-link-${i}`}
                    to={`/${b.slug}`}
                    className="inline-flex items-center bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-medium text-slate-700 hover:border-[#f97316] hover:text-[#0a2540] transition-all"
                  >
                    {b.brand}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* FOOTER */}
      <footer className="bg-[#0a2540] text-white border-t border-slate-800 py-12 mt-auto">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-8 text-left">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/10 rounded-lg text-white">
                <Tv className="h-6 w-6 text-[#f97316]" />
              </div>
              <div className="flex flex-col">
                <span className="font-extrabold text-xl tracking-tight text-white block leading-none">TV CARE</span>
                <span className="text-[10px] text-[#f97316] font-semibold uppercase tracking-wider mt-1">Your Tv Our Care</span>
              </div>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
              Friendly, same-day television repair brought right to your door. Whatever the make or model, our engineers get your screen working again &mdash; backed by a six-month promise.
            </p>
          </div>

          <div>
            <h4 className="font-bold text-sm text-[#f97316] uppercase tracking-wider mb-4">Reach Us</h4>
            <div className="space-y-2 text-xs text-slate-300">
              <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#f97316]" /> Home visits right across the city</p>
              <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#f97316]" /> Open every day, 10 AM &ndash; 9 PM</p>
              <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#f97316]" /> <a href="tel:8884340007" className="hover:underline">(888) 434-0007</a></p>
            </div>
          </div>

          <div>
            <h4 className="font-bold text-sm text-[#f97316] uppercase tracking-wider mb-4">Explore</h4>
            <div className="space-y-2 text-xs text-slate-300">
              <button onClick={() => setActiveTab("home")} className="block hover:underline">Home</button>
              <button onClick={() => setActiveTab("book")} className="block hover:underline">Request a Visit</button>
              <button onClick={() => setActiveTab("reviews")} className="block hover:underline">Customer Stories</button>
            </div>
          </div>

          <div>
            <h4 className="font-bold text-sm text-[#f97316] uppercase tracking-wider mb-4">Our Promise</h4>
            <p className="text-xs text-slate-300 leading-relaxed mb-3">
              Fit a part or fix a fault and it&apos;s covered for six full months. Fair, upfront pricing every time &mdash; never any nasty surprises on the bill.
            </p>
            <span className="inline-flex items-center gap-1 bg-[#10b981]/15 px-2.5 py-1 rounded-full text-[10px] font-bold text-[#10b981] uppercase tracking-wider">
              <ShieldCheck className="h-3.5 w-3.5" /> Six-Month Cover
            </span>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 border-t border-slate-800 mt-10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-slate-400">
          <p>© 2026 TV CARE. All rights reserved.</p>
          <div className="flex gap-4">
            <span className="hover:underline cursor-pointer">Terms of Service</span>
            <span className="hover:underline cursor-pointer">Privacy Policy</span>
          </div>
        </div>
      </footer>


    </div>
  );
}
